/**
 * Onboarding Firebase auth readiness hook
 *
 * Subscribes to Firebase Auth and reports whether the signed-in user's uid matches
 * the current onboarding `signUpId` from the Zustand store.
 *
 * Used by `useOnboardingSignUpGate` to choose pre-auth (invite callable only) vs
 * post-auth (Firestore `signedUpPatients` listener) loading rules.
 *
 * References:
 * - `hooks/useOnboardingSignUpGate.ts`
 * - `store/onboardingStore.ts`
 */

import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { auth } from '../../../firebaseConfig';
import { getSignUpId, useOnboardingStore } from '../store/onboardingStore';

/**
 * Whether Firebase Auth is aligned with the active onboarding sign-up id.
 *
 * @return {boolean | null} `null` while resolving; `true` when `auth.uid === signUpId`; else `false`.
 */
export function useOnboardingFirebaseAuthReady(): boolean | null {
  const signUpId = useOnboardingStore(getSignUpId);
  const [isReady, setIsReady] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!signUpId?.trim()) {
        setIsReady(false);
        return;
      }
      setIsReady(Boolean(user && user.uid === signUpId.trim()));
    });
    return unsubscribe;
  }, [signUpId]);

  return isReady;
}
